import { ActionIcon, Button, Tooltip } from "@mantine/core";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useInstatusStatus } from "@/api/resources/instatus/hooks";
import {
  getInstatusPageStatusColor,
  getInstatusPageStatusColorLight,
  hasInstatusPageIssue,
} from "@/api/resources/instatus/utils";
import { SYSTEM_COLORS } from "@/configs/colorScheme";
import { INSTATUS_PAGE_URL } from "@/configs/instatus";
import { useAppVersion } from "@/hooks/useAppVersion";
import { useIsMobile } from "@/hooks/useIsMobile";
import Icon from "@/utils/icons";

import styles from "@/components/AppStatus/AppStatus.module.css";

interface AppStatusProps {
  collapsed?: boolean;
}
export const AppStatus = ({ collapsed }: AppStatusProps) => {
  const appVersion = useAppVersion();
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { data: instatusStatus, isLoading: loadingInstatusStatus } = useInstatusStatus();

  const htmlRoot = document.documentElement;
  useEffect(() => {
    // set indicator color based on status
    if (instatusStatus) {
      const statusColor = getInstatusPageStatusColor(instatusStatus.page.status);
      const statusColorLight = getInstatusPageStatusColorLight(instatusStatus.page.status);
      htmlRoot.style.setProperty("--app-status-color", statusColor);
      htmlRoot.style.setProperty("--app-status-animation-color", statusColorLight);
    }
  }, [instatusStatus, htmlRoot]);

  // set icon based on status
  const hasIssues = instatusStatus ? hasInstatusPageIssue(instatusStatus.page.status) : false;
  const statusIcon = hasIssues ? (
    <div className={styles["app-status"]} />
  ) : (
    <Icon.DOT size={12} weight="fill" color={SYSTEM_COLORS.success} />
  );

  const instatusLabel = instatusStatus ? t(`instatus.status.${instatusStatus.page.status}`) : null;
  const tooltipLabel = `${appVersion ?? ""} ${instatusLabel ? `App ${instatusLabel}` : ""}`;

  if (collapsed ?? isMobile)
    return (
      <Tooltip label={tooltipLabel}>
        <ActionIcon
          loading={loadingInstatusStatus}
          component="a"
          href={INSTATUS_PAGE_URL}
          target="_blank"
          variant="transparent"
        >
          {statusIcon}
        </ActionIcon>
      </Tooltip>
    );

  return (
    <Tooltip label={tooltipLabel}>
      <Button
        variant="subtle"
        loading={loadingInstatusStatus}
        component="a"
        href={INSTATUS_PAGE_URL}
        target="_blank"
        leftSection={statusIcon}
      >
        {t("status").toUpperCase()}
      </Button>
    </Tooltip>
  );
};

export default AppStatus;
